/**
 * STEP OVERLAY-PRESET-UI-FIX-1 (2026-02) · Shared Preset Overlay (REUSE 완성).
 *
 * 공통 Overlay — Studio preview 카드 (SellerLiveConsole) ↔ 소비자 Viewer
 * (LiveKitViewer) ↔ 전면 overlay 가 **동일 렌더 정의**를 공유 (default / full /
 * price / options / mini 5종). 신규 Overlay 엔진 0 · 신규 polling 0 · 영상
 * 재인코딩/Egress 합성 없음 — React UI 레이어 only.
 *
 * STEP OVERLAY-PRESET-UI-FIX-1 보완 (판매자 요구사항):
 *   · 모든 프리셋에 실 상품 이미지 (SafeImg) — 좌측 썸네일 통일.
 *     (사이즈: default/full/options 48px · price 56px · mini 24px — md).
 *   · 전체정보형: product.shipping_fee 값에 따라 '무료배송' / '배송비 N원' —
 *     null/미등록 시 라인 자체 미표시 (fabrication 금지).
 *   · 옵션형: product.options[].name + values[].label 조합 — 그룹당 1줄,
 *     최대 2 그룹 · 그룹당 값 최대 3개. 실 등록 옵션만 (fabrication 금지).
 *   · 미니형: 작은 썸네일 + 상품명 + 가격 (브랜드 글자 하드코딩 제거).
 *
 * Props:
 *   · preset   : 'default' | 'full' | 'price' | 'options' | 'mini'
 *   · product  : { name, sale_price, image, options?, shipping_fee?, shipping_option? }
 *   · position : 'top' | 'middle' | 'bottom'  (default 'bottom')
 *   · hidden   : bool  (true 면 render null — overlay OFF 상태 재사용)
 *   · size     : 'md' (default · 소비자/풀스케일) | 'sm' (Studio 미리보기 카드 안)
 *   · orientation : 'horizontal' (기본 · 이미지 왼쪽) | 'vertical' (이미지 위 · 상품명·가격 아래)
 *   · free     : true 면 자체 absolute 위치 없이 부모(OverlayFreeBox) 폭 100% 로 렌더
 *
 * 외부라이브 (YouTube/Egress) EgressTemplate 는 자체 분기 유지 — 이 컴포넌트는
 * 자사라이브 React UI 레이어 전용 (Egress 영상 합성과 무관).
 */
import React from 'react';
import SafeImg from '@/components/SafeImg';

const _won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;

const PRESET_POSITION_CLS = {
  top:    'top-4 left-1/2 -translate-x-1/2',
  middle: 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
  bottom: 'bottom-6 left-1/2 -translate-x-1/2',
};

// 사이즈 토큰 — md (소비자/풀스케일) · sm (Studio 미리보기 카드 안).
// 동일 레이아웃/구조/데이터 사용 — 크기만 조정 (중복 구현 금지).
const SIZE_TOKENS = {
  md: {
    thumbDefault: 'w-12 h-12',       // 48px
    thumbPrice:   'w-14 h-14',       // 56px
    thumbMini:    'w-6 h-6',         // 24px
    padCard:      'px-3 py-2',
    padPrice:     'px-4 py-2.5',
    padMini:      'px-2.5 py-1',
    gap:          'gap-2',
    textName:     'text-[13px]',
    textPrice:    'text-[16px]',
    textPriceBig: 'text-[22px]',
    textSub:      'text-[10px]',
    textChipKey:  'text-[10px]',
    textChipVal:  'text-[10px]',
    textMiniName: 'text-[11px]',
    textMiniPrice:'text-[12px]',
    minWDefault:  'min-w-[220px]',
    minWFull:     'min-w-[240px]',
    minWPrice:    'min-w-[200px]',
    minWOptions:  'min-w-[260px]',
  },
  sm: {
    thumbDefault: 'w-6 h-6',         // 24px (preview card 축소)
    thumbPrice:   'w-7 h-7',         // 28px
    thumbMini:    'w-3.5 h-3.5',     // 14px
    padCard:      'px-1.5 py-1',
    padPrice:     'px-1.5 py-1',
    padMini:      'px-1.5 py-0.5',
    gap:          'gap-1',
    textName:     'text-[9px]',
    textPrice:    'text-[10px]',
    textPriceBig: 'text-[13px]',
    textSub:      'text-[8px]',
    textChipKey:  'text-[7px]',
    textChipVal:  'text-[7px]',
    textMiniName: 'text-[7px]',
    textMiniPrice:'text-[8px]',
    minWDefault:  'min-w-0',
    minWFull:     'min-w-0',
    minWPrice:    'min-w-0',
    minWOptions:  'min-w-0',
  },
};

// 전체정보형 배송정보 라인 — fabrication 금지. value 가 숫자(정수) 가 아니면 null.
// shipping_fee === 0 → 무료배송 · > 0 → "배송비 N원" · null/undefined → null (라인 숨김).
const _shippingLabel = (product) => {
  if (!product) return null;
  const raw = product.shipping_fee;
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  if (n <= 0) return '무료배송';
  return `배송비 ${n.toLocaleString('ko-KR')}원`;
};

// 옵션형 라인 — product.options (whitelist) 만 사용. 최대 2 그룹 · 그룹당 값 최대 3개.
// 반환: [{name, values:string[]}, ...] 또는 빈 배열 (fabrication 금지).
const _optionGroups = (product) => {
  const opts = Array.isArray(product.options) ? product.options : [];
  const out = [];
  for (const grp of opts.slice(0, 2)) {
    if (!grp || typeof grp !== 'object') continue;
    const gName = (grp.name || grp.option_name || '').toString().trim();
    const rawVals = Array.isArray(grp.values) ? grp.values : (Array.isArray(grp.option_values) ? grp.option_values : []);
    const vals = [];
    for (const v of rawVals.slice(0, 3)) {
      if (!v) continue;
      if (typeof v === 'string' || typeof v === 'number') {
        vals.push(String(v).trim());
      } else if (typeof v === 'object') {
        const lbl = (v.label || v.name || v.value || '').toString().trim();
        if (lbl) vals.push(lbl);
      }
    }
    if (gName || vals.length > 0) {
      out.push({ name: gName, values: vals });
    }
  }
  return out;
};
export { _won as formatWon, _optionGroups as optionGroups };

// 좌측 썸네일 (실 상품 이미지 · 없으면 회색 placeholder — fabrication 금지).
const Thumb = ({ product, sizeCls }) => (
  <div className={`${sizeCls} shrink-0 rounded-md overflow-hidden bg-gray-200 relative`}
       data-testid="live-preset-overlay-thumb">
    {product?.image ? (
      <SafeImg src={product.image} kind="product" alt=""
               className="absolute inset-0 w-full h-full object-cover" />
    ) : null}
  </div>
);

const LivePresetOverlay = ({ preset, product, position = 'bottom', hidden, size = 'md', orientation = 'horizontal', free = false, imageFit = 'cover' }) => {
  if (!preset || !product || hidden) return null;
  const T = SIZE_TOKENS[size] || SIZE_TOKENS.md;
  const name = product.name || '상품명';
  const price = product.sale_price;
  const posCls = PRESET_POSITION_CLS[position] || PRESET_POSITION_CLS.bottom;
  const wCls = free ? 'w-full !min-w-0' : '';

  let node = null;
  if (orientation === 'vertical') {
    // STEP STUDIO-OVERLAY-RESPONSIVE-PERSIST-1 · 세로형 — 이미지 위 · 번호·상품명 중단 · 가격 하단 · 옵션/사이즈 선택.
    //   카드 폭 기준 container query(cqw) 로 이미지·글자·간격이 함께 반응 · 높이는 콘텐츠에 맞춰 자동.
    const isPrice = preset === 'price';
    const isMini = preset === 'mini';
    const ship = preset === 'full' ? _shippingLabel(product) : null;
    const groups = (preset === 'options' || preset === 'full') ? _optionGroups(product).slice(0, 2) : [];
    const no = product.slot_number;
    const showNo = Number.isFinite(Number(no)) && no !== null && no !== '' && !String(name).startsWith(`${no}번`);
    node = (
      <div style={{ containerType: 'inline-size' }} className={free ? 'w-full' : 'w-[160px]'}
           data-testid="live-preset-overlay-vertical" data-preset={preset}>
        <div className={`${isPrice ? 'bg-[#FF3B30]' : 'bg-white/95'} shadow-xl flex flex-col items-stretch text-center overflow-hidden`}
             style={{ padding: '4cqw', gap: '2.5cqw', borderRadius: '5cqw' }}>
          <div className="w-full aspect-square overflow-hidden bg-gray-200 relative" style={{ borderRadius: '3.5cqw' }}
               data-testid="live-preset-overlay-thumb">
            {product?.image ? (
              <SafeImg src={product.image} kind="product" alt="" className={`absolute inset-0 w-full h-full ${imageFit === 'contain' ? 'object-contain' : 'object-cover'}`} />
            ) : null}
          </div>
          {!isMini && !isPrice && (
            <p className="font-black text-black break-keep line-clamp-3" style={{ fontSize: '7.5cqw', lineHeight: 1.3 }}
               data-testid="live-preset-overlay-vertical-name">
              {showNo && <span className="text-[#FF3B30]" style={{ marginRight: '1.5cqw' }}>{no}번</span>}
              {name}
            </p>
          )}
          <p className={`font-black whitespace-nowrap ${isPrice ? 'text-white' : 'text-[#FF3B30]'}`}
             style={{ fontSize: isPrice ? '12cqw' : '10cqw', lineHeight: 1.15 }}
             data-testid="live-preset-overlay-vertical-price">{_won(price)}</p>
          {ship ? (
            <p className="text-gray-600 font-bold" style={{ fontSize: '5.5cqw' }} data-testid="live-preset-overlay-shipping">{ship}</p>
          ) : null}
          {groups.map((g, gi) => (
            <div key={gi} className="flex flex-wrap justify-center" style={{ gap: '1.5cqw' }}>
              {g.name ? <span className="text-gray-500 font-bold" style={{ fontSize: '5cqw' }}>{g.name}</span> : null}
              {g.values.slice(0, 6).map((v, vi) => (
                <span key={vi} className="bg-gray-100 text-gray-800 font-black"
                      style={{ fontSize: '5.5cqw', padding: '0.8cqw 2.2cqw', borderRadius: '2cqw' }}>{v}</span>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  } else if (preset === 'default') {
    node = (
      <div className={`bg-white/95 rounded-lg shadow-xl ${T.padCard} ${T.minWDefault} ${wCls} inline-flex items-center ${T.gap}`}>
        <Thumb product={product} sizeCls={T.thumbDefault} />
        <div className="min-w-0 flex-1">
          <p className={`${T.textName} font-black text-black truncate`}>{name}</p>
          <p className={`${T.textPrice} font-black text-[#FF3B30] leading-tight`}>{_won(price)}</p>
        </div>
      </div>
    );
  } else if (preset === 'full') {
    const ship = _shippingLabel(product);
    node = (
      <div className={`bg-white/95 rounded-lg shadow-xl ${T.padCard} ${T.minWFull} ${wCls} inline-flex items-center ${T.gap}`}>
        <Thumb product={product} sizeCls={T.thumbDefault} />
        <div className="min-w-0 flex-1">
          <p className={`${T.textName} font-black text-black truncate`}>{name}</p>
          <p className={`${T.textPrice} font-black text-[#FF3B30] leading-tight`}>{_won(price)}</p>
          {ship ? (
            <p className={`${T.textSub} text-gray-600 mt-0.5 truncate`}
               data-testid="live-preset-overlay-shipping">{ship}</p>
          ) : null}
        </div>
      </div>
    );
  } else if (preset === 'price') {
    node = (
      <div className={`bg-[#FF3B30] rounded-lg shadow-xl ${T.padPrice} ${T.minWPrice} ${wCls} inline-flex items-center ${T.gap}`}>
        <Thumb product={product} sizeCls={T.thumbPrice} />
        <p className={`${T.textPriceBig} font-black text-white leading-none`}>{_won(price)}</p>
      </div>
    );
  } else if (preset === 'options') {
    const groups = _optionGroups(product);
    node = (
      <div className={`bg-white/95 rounded-lg shadow-xl ${T.padCard} ${T.minWOptions} ${wCls} inline-flex items-center ${T.gap}`}>
        <Thumb product={product} sizeCls={T.thumbDefault} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <p className={`${T.textName} font-black text-black truncate flex-1`}>{name}</p>
            <span className={`${T.textPrice} font-black text-[#FF3B30] shrink-0`}>{_won(price)}</span>
          </div>
          {groups.length > 0 ? (
            <div className="mt-0.5 flex flex-col gap-0.5" data-testid="live-preset-overlay-options">
              {groups.map((g, gi) => (
                <div key={gi} className="flex items-baseline gap-1 min-w-0">
                  {g.name ? (
                    <span className={`${T.textChipKey} font-black text-gray-500 shrink-0`}>{g.name}:</span>
                  ) : null}
                  <span className={`${T.textChipVal} font-black text-gray-800 truncate`}>
                    {g.values.length > 0 ? g.values.join('·') : '등록된 값 없음'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className={`${T.textChipKey} font-bold text-gray-400 mt-0.5`}
               data-testid="live-preset-overlay-options-empty">{'옵션 미등록'}</p>
          )}
        </div>
      </div>
    );
  } else if (preset === 'mini') {
    node = (
      <div className={`bg-white/95 rounded-full shadow-xl ${T.padMini} ${free ? 'w-full justify-center' : ''} inline-flex items-center ${T.gap}`}>
        <Thumb product={product} sizeCls={T.thumbMini} />
        <span className={`${T.textMiniName} font-black text-black truncate max-w-[120px]`}>{name}</span>
        <span className={`${T.textMiniPrice} font-black text-[#FF3B30]`}>{_won(price)}</span>
      </div>
    );
  }
  if (!node) return null;
  if (free) {
    return (
      <div className="pointer-events-none w-full flex justify-center"
           data-testid="live-preset-overlay" data-preset={preset} data-free="1" data-orientation={orientation} data-size={size}>
        {node}
      </div>
    );
  }
  return (
    <div className={`absolute z-10 pointer-events-none ${posCls}`}
          data-testid="live-preset-overlay"
          data-preset={preset}
          data-position={position}
          data-size={size}>
      {node}
    </div>
  );
};

export default LivePresetOverlay;
export { PRESET_POSITION_CLS, SIZE_TOKENS };
